import React from "react";
import Navbar from "../components/Navbar";
import ScrollReveal from "../components/ScrollReveal";
import SEO from "../components/SEO";
import {
  BsShieldCheck,
  BsFire,
  BsSun,
  BsWater,
  BsWind,
  BsSnow,
  BsBrush,
  BsStars,
  BsCloudRain,
  BsClockHistory,
} from "react-icons/bs";

const GuideCard = ({ icon: Icon, title, children }) => (
  <div className="bg-gradient-to-br from-sky-500 to-blue-600 dark:from-sky-600 dark:to-blue-800 p-8 rounded-3xl shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 flex flex-col h-full border border-blue-400/20 group">
    <div className="w-14 h-14 bg-white/20 backdrop-blur-md text-white rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-inner">
      <Icon size={28} />
    </div>
    <h3 className="text-xl font-bold text-white mb-3 tracking-tight">
      {title}
    </h3>
    <p className="text-blue-50/90 text-[15px] leading-relaxed font-medium">
      {children}
    </p>
  </div>
);

const CareGuide = () => {
  return (
    <div 
      className="bg-gray-50 dark:bg-slate-950 min-h-screen flex flex-col transition-colors duration-500"
      style={{ 
        zoom: "0.8",
        MozTransform: "scale(0.8)",
        MozTransformOrigin: "top center"
      }}
    >
      <SEO
        title="Hướng dẫn sử dụng & Bảo quản"
        description="Hướng dẫn chi tiết cách sử dụng, bảo quản và vệ sinh giày thể thao đúng cách để bền đẹp lâu dài."
      />
      <Navbar />

      <main className="flex-grow pt-44 pb-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Hero Section */}
          <ScrollReveal variants="fade-up">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 dark:text-white mb-6 tracking-tight uppercase">
                HƯỚNG DẪN SỬ DỤNG <br />
                <span className="text-blue-600 dark:text-blue-500">
                  BẢO QUẢN & VỆ SINH GIÀY
                </span>
              </h1>
              <div className="w-24 h-1.5 bg-blue-600 mx-auto rounded-full mb-6" />
              <p className="max-w-2xl mx-auto text-gray-500 dark:text-slate-400 text-lg">
                Để đôi giày của bạn luôn bền đẹp và đồng hành cùng bạn trên mọi
                nẻo đường, hãy cùng TaHa <span className="text-red-600 font-bold">Shoes</span> tìm hiểu cách chăm sóc giày
                chuyên nghiệp nhé!
              </p>
            </div>
          </ScrollReveal>

          {/* Section 1: Usage & Care */}
          <section className="mb-20">
            <ScrollReveal variants="fade-up" distance={30}>
              <div className="flex items-center gap-4 mb-10">
                <div className="h-8 w-2 bg-blue-600 rounded-full" />
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white uppercase">
                  1. HƯỚNG DẪN SỬ DỤNG VÀ BẢO QUẢN HIỆU QUẢ
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: BsFire,
                  title: "Tránh nhiệt độ cao",
                  desc: "Không cho giày tiếp xúc trực tiếp dưới lửa hoặc nguồn nhiệt lớn để tránh làm hỏng keo và biến dạng chất liệu.",
                },
                {
                  icon: BsWater,
                  title: "Hạn chế ngâm nước",
                  desc: "Không ngâm giày quá lâu trong nước vì sẽ làm mục chất liệu vải và bong tróc lớp keo dán.",
                },
                {
                  icon: BsSun,
                  title: "Phơi nắng đúng cách",
                  desc: "Tránh phơi giày trực tiếp dưới ánh nắng gay gắt trong thời gian dài để bảo vệ màu sắc nguyên bản.",
                },
                {
                  icon: BsShieldCheck,
                  title: "Tránh hóa chất",
                  desc: "Cẩn thận không để dầu mỡ hoặc các chất kết dính rơi trực tiếp vào giày, gây hư hại bề mặt.",
                },
                {
                  icon: BsWind,
                  title: "Nơi khô ráo",
                  desc: "Luôn bảo quản giày ở nơi khô ráo, thoáng mát, tránh ẩm ướt để ngăn chặn nấm mốc phát triển.",
                },
                {
                  icon: BsStars,
                  title: "Vệ sinh sau khi dùng",
                  desc: "Lau chùi nhẹ nhàng sau khi sử dụng để loại bỏ bụi bẩn, giúp giày luôn sạch và không bị mùi hôi.",
                },
              ].map((item, i) => (
                <ScrollReveal key={i} variants="fade-up" delay={i * 0.1}>
                  <GuideCard icon={item.icon} title={item.title}>
                    {item.desc}
                  </GuideCard>
                </ScrollReveal>
              ))}
            </div>
          </section>

          {/* Section 2: Cleaning */}
          <section>
            <ScrollReveal variants="fade-up" distance={30}>
              <div className="flex items-center gap-4 mb-10">
                <div className="h-8 w-2 bg-blue-600 rounded-full" />
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white uppercase">
                  2. CÁCH VỆ SINH GIÀY ĐÚNG CÁCH
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <ScrollReveal variants="fade-up" delay={0.1}>
                <GuideCard icon={BsBrush} title="Dùng bàn chải mềm">
                  Tuyệt đối không dùng bàn chải cứng để chà rửa. Hãy dùng bàn
                  chải lông mềm hoặc khăn ẩm để lau chùi bề mặt giày.
                </GuideCard>
              </ScrollReveal>

              <ScrollReveal variants="fade-up" delay={0.2}>
                <GuideCard icon={BsSnow} title="Hạn chế chất tẩy">
                  Không dùng chất tẩy mạnh (như thuốc tẩy quần áo) đổ trực tiếp
                  vào giày, đặc biệt là với các dòng giày màu.
                </GuideCard>
              </ScrollReveal>

              <ScrollReveal variants="fade-up" delay={0.3}>
                <GuideCard icon={BsStars} title="Dùng dung dịch chuyên dụng">
                  Sử dụng các chai tẩy trắng giày như Plac để làm sạch các vết
                  bẩn cứng đầu một cách an toàn và hiệu quả.
                </GuideCard>
              </ScrollReveal>

              <ScrollReveal variants="fade-up" delay={0.4}>
                <GuideCard icon={BsCloudRain} title="Bảo vệ khi trời mưa">
                  Nên sử dụng chai xịt chống thấm nano (ví dụ: EYKOSI) để tạo
                  lớp màng bảo vệ giày khỏi nước mưa và bùn đất.
                </GuideCard>
              </ScrollReveal>

              <ScrollReveal variants="fade-up" delay={0.5}>
                <GuideCard icon={BsClockHistory} title="Vệ sinh thường xuyên">
                  Đừng đợi đến khi giày quá bẩn mới giặt. Vệ sinh định kỳ giúp
                  tăng tuổi thọ và giữ form giày lâu hơn.
                </GuideCard>
              </ScrollReveal>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default CareGuide;
